//CampaignsPage.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextareaAutosize,
  IconButton,
  Tooltip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add,
  PlayArrow,
  Pause,
  Stop,
  Edit,
  Delete,
  Analytics,
  Schedule,
  Group,
  Message,
  TrendingUp,
  Email,
  Phone,
  Web
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';
import { formatDate } from '../utils/dateUtils';

interface Campaign {
  id: number;
  title: string;
  description?: string;
  campaign_type: 'one_off' | 'ongoing';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  trigger_type: 'immediate' | 'scheduled' | 'event_based';
  scheduled_at?: string;
  audience_type: string;
  message_content: string;
  message_type: string;
  enabled_channels: string[];
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  replied_count: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface CampaignTemplate {
  id: number;
  name: string;
  description?: string;
  category: string;
  subject?: string;
  message_content: string;
  message_type: string;
  variables: string[];
  usage_count: number;
  created_at: string;
}

// Phase 4: WhatsApp Template Integration (2026-01-19)
interface WhatsAppTemplate {
  id: number;
  name: string;
  category: string;
  language_code: string;
  components: any[];
  variables: string[];
  approval_status: string;
  quality_score?: string;
}

const CampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  // Phase 4: WhatsApp Template Integration (2026-01-19)
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [showTemplateSettings, setShowTemplateSettings] = useState(false);

  interface FormData {
    title: string;
    description: string;
    campaign_type: 'one_off' | 'ongoing';
    trigger_type: 'immediate' | 'scheduled' | 'event_based';
    scheduled_at: Date | null;
    audience_type: string;
    audience_filters: Record<string, any>;
    message_content: string;
    message_type: string;
    media_url: string;
    enabled_channels: string[];
    send_only_business_hours: boolean;
    // Phase 4: WhatsApp Template Integration (2026-01-19)
    whatsapp_template_id: number | null;
    template_variables: Record<string, string>;
    rate_limit_per_minute: number;
    retry_failed: boolean;
    max_retries: number;
    enforce_24h_window: boolean;
    template_cooldown_hours: number;
    template_category: string;
  }
  
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    campaign_type: 'one_off',
    trigger_type: 'immediate',  // ✅ not `as const`
    scheduled_at: null,
    audience_type: 'all',
    audience_filters: {},
    message_content: '',
    message_type: 'text',
    media_url: '',
    enabled_channels: ['website'],
    send_only_business_hours: false,
    // Phase 4: WhatsApp Template Integration (2026-01-19)
    whatsapp_template_id: null,
    template_variables: {},
    rate_limit_per_minute: 100,
    retry_failed: true,
    max_retries: 3,
    enforce_24h_window: true,
    template_cooldown_hours: 48,
    template_category: ''
  });
  

  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'general',
    subject: '',
    message_content: '',
    message_type: 'text',
    variables: [] as string[]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch campaigns
      const campaignsResponse = await api.get('/campaigns/');
      setCampaigns(campaignsResponse.data.campaigns || []);

      // Fetch templates
      const templatesResponse = await api.get('/campaigns/templates/');
      setTemplates(templatesResponse.data.templates || []);

      // Phase 4: Fetch WhatsApp templates for campaign integration (2026-01-19)
      try {
        const whatsappTemplatesResponse = await api.get('/campaigns/whatsapp-templates/');
        setWhatsappTemplates(whatsappTemplatesResponse.data.templates || []);
      } catch (err) {
        console.log('WhatsApp templates not available:', err);
        setWhatsappTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const payload = {
        ...formData,
        scheduled_at: formData.scheduled_at?.toISOString()
      };

      await api.post('/campaigns/', payload);
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await api.post('/campaigns/templates/', templateForm);
      setIsTemplateDialogOpen(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const handleCampaignAction = async (campaignId: number, action: string) => {
    try {
      console.log(`Attempting to ${action} campaign ${campaignId}`);
      await api.post(`/campaigns/${campaignId}/${action}`);
      console.log(`Successfully ${action}ed campaign ${campaignId}`);
      fetchData();
    } catch (error: any) {
      console.error(`Error ${action} campaign:`, error);
      const message = error.response?.data?.detail || `Failed to ${action} campaign`;
      console.log(`Campaign ${campaignId} current status might not allow ${action} action`);
      
      // Force refresh the data to make sure we have the latest status
      fetchData();
      
      alert(`Error: ${message}\n\nThe campaign status has been refreshed. Please check the current status before trying again.`);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      campaign_type: 'one_off',
      trigger_type: 'immediate',
      scheduled_at: null,
      audience_type: 'all',
      audience_filters: {},
      message_content: '',
      message_type: 'text',
      media_url: '',
      enabled_channels: ['website'],
      send_only_business_hours: false,
      // Phase 4: WhatsApp Template Integration (2026-01-19)
      whatsapp_template_id: null,
      template_variables: {},
      rate_limit_per_minute: 100,
      retry_failed: true,
      max_retries: 3,
      enforce_24h_window: true,
      template_cooldown_hours: 48,
      template_category: ''
    });
    setShowTemplateSettings(false);
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      name: '',
      description: '',
      category: 'general',
      subject: '',
      message_content: '',
      message_type: 'text',
      variables: []
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return <Email />;
      case 'whatsapp': return <Phone />;
      case 'website': return <Web />;
      default: return <Message />;
    }
  };

  const calculateEngagementRate = (campaign: Campaign) => {
    if (campaign.delivered_count === 0) return 0;
    return Math.round(((campaign.opened_count + campaign.clicked_count + campaign.replied_count) / campaign.delivered_count) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Campaigns
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setIsTemplateDialogOpen(true)}
            >
              New Template
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setIsCreateDialogOpen(true)}
            >
              New Campaign
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
            <Tab label="Campaigns" />
            <Tab label="Templates" />
            <Tab label="Analytics" />
          </Tabs>
        </Box>

        {/* Campaigns Tab */}
        {currentTab === 0 && (
          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                <Card>
                  <CardHeader
                    title={campaign.title}
                    subheader={campaign.description}
                    action={
                      <Chip
                        label={campaign.status}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    }
                  />
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Channels
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {campaign.enabled_channels.map((channel) => (
                          <Tooltip key={channel} title={channel}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getChannelIcon(channel)}
                            </Box>
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Recipients: {campaign.total_recipients}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sent: {campaign.sent_count} | Delivered: {campaign.delivered_count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Engagement: {calculateEngagementRate(campaign)}%
                      </Typography>
                    </Box>

                    {campaign.delivered_count > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          Performance
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={calculateEngagementRate(campaign)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                  <CardActions>
                    {campaign.status === 'draft' && (
                      <Button
                        size="small"
                        startIcon={<PlayArrow />}
                        onClick={() => handleCampaignAction(campaign.id, 'start')}
                      >
                        Start
                      </Button>
                    )}
                    {campaign.status === 'active' && (
                      <Button
                        size="small"
                        startIcon={<Pause />}
                        onClick={() => handleCampaignAction(campaign.id, 'pause')}
                      >
                        Pause
                      </Button>
                    )}
                    {campaign.status === 'paused' && (
                      <Button
                        size="small"
                        startIcon={<PlayArrow />}
                        onClick={() => handleCampaignAction(campaign.id, 'resume')}
                      >
                        Resume
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<Analytics />}
                      onClick={() => setSelectedCampaign(campaign)}
                    >
                      Analytics
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Templates Tab */}
        {currentTab === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Usage Count</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {template.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={template.category} size="small" />
                    </TableCell>
                    <TableCell>{template.message_type}</TableCell>
                    <TableCell>{template.usage_count}</TableCell>
                    <TableCell>
                      //{new Date(template.created_at).toLocaleDateString()}
					  {formatDate(template.created_at)}
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          message_content: template.message_content,
                          message_type: template.message_type
                        }));
                        setIsCreateDialogOpen(true);
                      }}>
                        Use Template
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Analytics Tab */}
        {currentTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Campaign Performance" />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Total Campaigns: {campaigns.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active: {campaigns.filter(c => c.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed: {campaigns.filter(c => c.status === 'completed').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Overall Engagement" />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {campaigns.reduce((sum, c) => sum + c.delivered_count, 0)} Messages Delivered
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Opened: {campaigns.reduce((sum, c) => sum + c.opened_count, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Replied: {campaigns.reduce((sum, c) => sum + c.replied_count, 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Create Campaign Dialog */}
        <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create New Campaign</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Campaign Title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Campaign Type</InputLabel>
                    <Select
                      value={formData.campaign_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, campaign_type: e.target.value as any }))}
                    >
                      <MenuItem value="one_off">One-time</MenuItem>
                      <MenuItem value="ongoing">Ongoing</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Trigger Type</InputLabel>
                    <Select
                      value={formData.trigger_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, trigger_type: e.target.value as any }))}
                    >
                      <MenuItem value="immediate">Send Immediately</MenuItem>
                      <MenuItem value="scheduled">Schedule for Later</MenuItem>
                      <MenuItem value="event_based">Event-based</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {formData.trigger_type === 'scheduled' && (
                  <Grid item xs={12} sm={6}>
                    <DateTimePicker
                      label="Scheduled Time"
                      value={formData.scheduled_at}
                      onChange={(date: Date | null) => setFormData(prev => ({ ...prev, scheduled_at: date }))}
                      //renderInput={(params) => <TextField {...params} fullWidth />}
                      slotProps={{
                        textField: { fullWidth: true }
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message Content"
                    value={formData.message_content}
                    onChange={(e) => setFormData(prev => ({ ...prev, message_content: e.target.value }))}
                    helperText="Use {{first_name}}, {{email}}, etc. for personalization"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.send_only_business_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, send_only_business_hours: e.target.checked }))}
                      />
                    }
                    label="Send only during business hours"
                  />
                </Grid>

                {/* Phase 4: WhatsApp Template Integration (2026-01-19) */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    WhatsApp Template Settings
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>WhatsApp Template</InputLabel>
                    <Select
                      value={formData.whatsapp_template_id || ''}
                      onChange={(e) => {
                        const templateId = e.target.value ? Number(e.target.value) : null;
                        setFormData(prev => ({ ...prev, whatsapp_template_id: templateId }));
                        if (templateId) {
                          setShowTemplateSettings(true);
                          // Auto-add whatsapp to enabled channels
                          if (!formData.enabled_channels.includes('whatsapp')) {
                            setFormData(prev => ({
                              ...prev,
                              enabled_channels: [...prev.enabled_channels, 'whatsapp']
                            }));
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>None (Use custom message)</em>
                      </MenuItem>
                      {whatsappTemplates.map((template) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name} ({template.category})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Template Category Filter</InputLabel>
                    <Select
                      value={formData.template_category}
                      onChange={(e) => setFormData(prev => ({ ...prev, template_category: e.target.value }))}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      <MenuItem value="MARKETING">Marketing</MenuItem>
                      <MenuItem value="UTILITY">Utility</MenuItem>
                      <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.whatsapp_template_id && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        WhatsApp template selected. Configure rate limiting and session settings below.
                      </Alert>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Rate Limit (per minute)"
                        value={formData.rate_limit_per_minute}
                        onChange={(e) => setFormData(prev => ({ ...prev, rate_limit_per_minute: parseInt(e.target.value) || 100 }))}
                        helperText="Max messages per minute"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max Retries"
                        value={formData.max_retries}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_retries: parseInt(e.target.value) || 3 }))}
                        helperText="Retry failed sends"
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Cooldown (hours)"
                        value={formData.template_cooldown_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, template_cooldown_hours: parseInt(e.target.value) || 48 }))}
                        helperText="Min hours between template sends"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.enforce_24h_window}
                            onChange={(e) => setFormData(prev => ({ ...prev, enforce_24h_window: e.target.checked }))}
                          />
                        }
                        label="Enforce 24-hour session window"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.retry_failed}
                            onChange={(e) => setFormData(prev => ({ ...prev, retry_failed: e.target.checked }))}
                          />
                        }
                        label="Retry failed messages"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCampaign} variant="contained">Create Campaign</Button>
          </DialogActions>
        </Dialog>

        {/* Create Template Dialog */}
        <Dialog open={isTemplateDialogOpen} onClose={() => setIsTemplateDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create Campaign Template</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Template Name"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={templateForm.category}
                      onChange={(e) => setTemplateForm(prev => ({ ...prev, category: e.target.value }))}
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="welcome">Welcome</MenuItem>
                      <MenuItem value="promotional">Promotional</MenuItem>
                      <MenuItem value="support">Support</MenuItem>
                      <MenuItem value="re_engagement">Re-engagement</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    value={templateForm.description}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Message Content"
                    value={templateForm.message_content}
                    onChange={(e) => setTemplateForm(prev => ({ ...prev, message_content: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTemplate} variant="contained">Create Template</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default CampaignsPage;